import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { DOOR_WIDTH, WALL_HEIGHT, WALL_THICKNESS, type RoomLayout } from './layout';

export const OFFICE_LEAF_WIDTH = DOOR_WIDTH / 2 - 0.01;
export const OFFICE_HINGE_X = DOOR_WIDTH / 2;
export const OFFICE_LEAF_HEIGHT = WALL_HEIGHT - 0.4 - 0.125 - 0.025;
export const OFFICE_LEAF_INSET = OFFICE_LEAF_WIDTH / 2 + 0.055;

/** One continuous jamb from the corridor wall through to the existing hinge line. */
export function officeDoorFrame(room: RoomLayout): { z: number; depth: number; faceZ: number } {
  const corridorFace = room.doorPosition[2] + (WALL_THICKNESS + 0.05) / 2;
  const roomFace = room.rect.z1 - 0.07;
  return { z: (corridorFace + roomFace) / 2, depth: corridorFace - roomFace, faceZ: roomFace - 0.01 };
}

/** Keep only the original right-hand leaf; the added left leaf intersected the chair. */
export function buildFittedOfficeDoor(centerY: number): THREE.Group {
  const root = new THREE.Group(); root.name = 'office-fitted-door';
    const leaf = buildOfficeDoor(0);
    const sy = OFFICE_LEAF_HEIGHT / 1.98;
    leaf.scale.set(1, sy, OFFICE_LEAF_WIDTH / 0.88);
    leaf.position.set(0, 0.01 + OFFICE_LEAF_HEIGHT / 2 - centerY, 0);
    // Keep the grip at a usable height rather than stretching the hardware with the leaf.
    for (const part of leaf.children) {
      if (/^door-(handle|lever|lock|latch)/.test(part.name)) {
        part.position.y += (1.04 - (0.01 + OFFICE_LEAF_HEIGHT / 2 - 0.01 * sy)) / sy;
        part.scale.y = 1 / sy;
      }
    }
    root.add(leaf);
  return root;
}

/** A held-open office door. Local X = thickness, Z = leaf width; hinge at +Z. */
export function buildOfficeDoor(centerY: number): THREE.Group {
  const root = new THREE.Group(); root.name = 'office-door-detailed';
  const paint = new THREE.MeshStandardMaterial({ color: 0xc6ceca, metalness: 0.18, roughness: 0.48 });
  const edge = new THREE.MeshStandardMaterial({ color: 0x7e898b, metalness: 0.65, roughness: 0.34 });
  const steel = new THREE.MeshStandardMaterial({ color: 0xb1b9bb, metalness: 0.85, roughness: 0.26 });
  const rubber = new THREE.MeshStandardMaterial({ color: 0x343d3e, roughness: 0.94 });
  const glass = new THREE.MeshStandardMaterial({ color: 0xc5dfe0, roughness: 0.28,
    transparent: true, opacity: 0.3, depthWrite: false, side: THREE.DoubleSide });
  const box = (name: string, size: [number, number, number], at: [number, number, number], material: THREE.Material) => {
    const mesh = new THREE.Mesh(new RoundedBoxGeometry(...size, 1, Math.min(0.003, ...size.map(v => v / 4))), material);
    mesh.name = name; mesh.position.set(at[0], centerY + at[1], at[2]);
    mesh.castShadow = material !== glass; mesh.receiveShadow = true;
    root.add(mesh); return mesh;
  };
  // Four rails surround a real opening: glazing is not pasted on an opaque slab.
  box('door-bottom-panel', [0.044, 1.12, 0.88], [0, -0.43, 0], paint);
  box('door-top-rail', [0.044, 0.2, 0.88], [0, 0.89, 0], paint);
  for (const z of [-0.305, 0.305]) box('door-stile', [0.044, 0.66, 0.27], [0, 0.46, z], paint);
  box('door-vision-glass', [0.006, 0.66, 0.34], [0, 0.46, 0], glass);
  for (const x of [-0.025, 0.025]) {
    for (const z of [-0.175, 0.175]) box('door-glazing-bead', [0.009, 0.68, 0.016], [x, 0.46, z], edge);
    for (const y of [0.125, 0.795]) box('door-glazing-bead', [0.009, 0.016, 0.36], [x, y, 0], edge);
    box('door-kick-plate', [0.004, 0.18, 0.81], [x, -0.86, 0], steel);
    box('door-handle-backplate', [0.008, 0.18, 0.045], [x, -0.05, -0.34], steel);
    box('door-lever-neck', [0.045, 0.018, 0.02], [Math.sign(x) * 0.046, -0.01, -0.34], steel);
    box('door-lever-grip', [0.02, 0.023, 0.12], [Math.sign(x) * 0.075, -0.01, -0.29], steel);
    box('door-lock-slot', [0.009, 0.025, 0.006], [x * 1.2, -0.09, -0.34], rubber);
  }
  box('door-latch-face', [0.035, 0.15, 0.004], [0, -0.02, -0.442], steel);
  for (const y of [-0.75, 0, 0.75]) {
    const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.012, 0.105, 12), steel);
    hinge.name = 'door-hinge'; hinge.position.set(0.034, centerY + y, 0.43); hinge.castShadow = true;
    root.add(hinge);
    box('door-hinge-leaf', [0.006, 0.10, 0.055], [0.025, y, 0.402], steel);
  }
  box('door-closer-body', [0.04, 0.052, 0.2], [0.044, 0.88, 0.27], edge);
  box('door-closer-track', [0.016, 0.015, 0.25], [0.054, 0.94, 0.27], steel);
  return root;
}
